import { QueryClient } from "@tanstack/react-query";
import QueryKeys from "../api/queryKeys";
import {
  hgdbSMPWUserPermission,
  hgdbSMPWZoneDefault,
  SMPWInvite,
  SMPWLocation,
  SMPWReport,
  SMPWSchedule,
  SMPWUser,
  SMPWUserAvailability,
  SMPWUserLocationAssignmentMask,
  SMPWZoneDefaults,
} from "../types/smpw";
import { SMPWAPI } from "../api/smpw";
import { Congregation } from "../types/scheduling/weekend";
import { groupBy } from "../helpers/util";
import { dateToString } from "../helpers/dateHelpers";
import dayjs from "../helpers/dayjs";

export const updateSMPWUserCache = (queryClient: QueryClient, newUser: SMPWUser, isNew = false) => {
  const newU = SMPWAPI.userFromResponse(newUser);
  if (isNew) {
    queryClient.setQueryData<SMPWUser[]>([QueryKeys.SMPWUsers], (old) => (old ? [...old, newU] : [newU]));
    return;
  }
  queryClient.setQueryData<SMPWUser[]>([QueryKeys.SMPWUsers], (old) =>
    old ? old.map((u) => (u.id === newU.id ? newU : u)) : [newU],
  );
};

export const updateSMPWUserPermissionsCache = (queryClient: QueryClient, newPerms: hgdbSMPWUserPermission[]) => {
  newPerms.forEach((newPerm) => {
    queryClient.setQueryData<hgdbSMPWUserPermission[]>([QueryKeys.SMPWUserPermissions], (old) => {
      if (!old) return [newPerm];
      if (!old.some((p) => p.user_id === newPerm.user_id)) return [...old, newPerm];
      return old.map((p) => (p.user_id === newPerm.user_id ? newPerm : p));
    });
  });
};

export function deleteFromSMPWUserCache(queryClient: QueryClient, user: SMPWUser) {
  queryClient.setQueriesData<SMPWUser[]>({ queryKey: [QueryKeys.SMPWUsers] }, (old) =>
    old ? old.filter((u) => u.id !== user.id) : [],
  );
}

export function deleteFromSMPWInviteCache(queryClient: QueryClient, invite: SMPWInvite) {
  queryClient.setQueriesData<SMPWInvite[]>({ queryKey: [QueryKeys.MySMPWInvites] }, (old) =>
    old ? old.filter((i) => i.id !== invite.id) : [],
  );
  queryClient.setQueriesData<SMPWInvite[]>({ queryKey: [QueryKeys.SMPWZoneInvites] }, (old) =>
    old ? old.filter((i) => i.id !== invite.id) : [],
  );
}

export const updateSMPWInviteCache = (queryClient: QueryClient, newInv: SMPWInvite) => {
  queryClient.setQueryData<SMPWInvite[]>([QueryKeys.SMPWZoneInvites], (old) => {
    if (!old) return [newInv];
    if (!old.some((inv) => inv.id === newInv.id)) return [...old, newInv];
    return old.map((inv) => (inv.id === newInv.id ? newInv : inv));
  });
};

export const updateSMPWLocationCache = (queryClient: QueryClient, newLoc: SMPWLocation) => {
  queryClient.setQueryData<SMPWLocation[]>([QueryKeys.SMPWLocations], (old) => {
    if (!old) return [newLoc];
    if (!old.some((l) => l.id === newLoc.id)) return [...old, newLoc];
    return old.map((l) => (l.id === newLoc.id ? newLoc : l));
  });
};

export function deleteFromSMPWLocationCache(queryClient: QueryClient, loc: SMPWLocation) {
  queryClient.setQueriesData<SMPWLocation[]>({ queryKey: [QueryKeys.SMPWLocations] }, (old) =>
    old ? old.filter((l) => l.id !== loc.id) : [],
  );
}

export const updateSMPWZoneDefaultCache = (queryClient: QueryClient, newZd: hgdbSMPWZoneDefault) => {
  queryClient.setQueryData<SMPWZoneDefaults[]>([QueryKeys.SMPWZoneDefaults], (old) => {
    if (!old) {
      return [{ zone_id: newZd.zone_id, actual: [newZd], calculated: [newZd] }];
    }
    const exists = old.some((zd) => zd.zone_id === newZd.zone_id);
    if (!exists) {
      return [...old, { zone_id: newZd.zone_id, actual: [newZd], calculated: [newZd] }];
    }
    return old.map((zd) => {
      if (zd.zone_id !== newZd.zone_id) return zd;
      const actualExists = zd.actual.some((a) => a.id === newZd.id);
      return {
        ...zd,
        actual: actualExists ? zd.actual.map((a) => (a.id === newZd.id ? newZd : a)) : [...zd.actual, newZd],
      };
    });
  });
};

export function deleteFromSMPWZoneDefaultCache(queryClient: QueryClient, zd: hgdbSMPWZoneDefault) {
  queryClient.setQueriesData<SMPWZoneDefaults[]>({ queryKey: [QueryKeys.SMPWZoneDefaults] }, (old) => {
    if (!old) return [];
    return old.map((entry) => {
      if (entry.zone_id !== zd.zone_id) return entry;
      return { ...entry, actual: entry.actual.filter((a) => a.id !== zd.id) };
    });
  });
}

export const updateSMPWUserAvailabilities = (queryClient: QueryClient, newAvails: SMPWUserAvailability[]) => {
  if (!newAvails.length) return;
  const userId = newAvails[0]?.user_id;
  if (userId === undefined) return;

  queryClient.setQueryData<SMPWUserAvailability[]>([QueryKeys.SMPWUserAvailabilities], (old) => {
    if (!old) return newAvails;
    const filteredOld = old.filter((a) => a.user_id !== userId);
    return [...filteredOld, ...newAvails];
  });
};

export const updateSMPWSchedulesCache = (
  queryClient: QueryClient,
  zone_id: number,
  incomingSchedules: SMPWSchedule[],
  from: string,
  to: string,
): void => {
  if (!incomingSchedules.length) return;

  const sKey = (s: SMPWSchedule): string => {
    return `${s.id}_${s.datetime.toISOString()}`;
  };

  const locSchedMap = groupBy(incomingSchedules, (s) => s.location_id);
  locSchedMap.forEach((incoming_schedules, loc_id) => {
    queryClient.setQueryData<SMPWSchedule[]>([QueryKeys.SMPWLocSchedules, loc_id, from, to], (existingSchedules) => {
      if (!existingSchedules) return incoming_schedules;
      const existingSchedulesMap = new Map(existingSchedules.map((schedule) => [sKey(schedule), schedule]));
      incoming_schedules.forEach((schedule) => existingSchedulesMap.set(sKey(schedule), schedule));
      return Array.from(existingSchedulesMap.values());
    });
  });

  const dateSchedMap = groupBy(incomingSchedules, (s) => s.datetime);
  dateSchedMap.forEach((incoming_schedules, datetime) => {
    const date = dateToString(datetime.toDate());
    queryClient.setQueryData<SMPWSchedule[]>(
      [QueryKeys.SMPWZoneSchedules, zone_id, date, date],
      (existingSchedules) => {
        if (!existingSchedules) return incoming_schedules;
        const existingSchedulesMap = new Map(existingSchedules.map((schedule) => [sKey(schedule), schedule]));
        incoming_schedules.forEach((schedule) => existingSchedulesMap.set(sKey(schedule), schedule));
        return Array.from(existingSchedulesMap.values());
      },
    );
  });
};

export function deleteSMPWSchedules(queryClient: QueryClient, schedules: SMPWSchedule[]) {
  queryClient.setQueriesData<SMPWSchedule[]>({ queryKey: [QueryKeys.SMPWLocSchedules] }, (old) =>
    old ? old.filter((s) => !schedules.includes(s)) : [],
  );
  queryClient.setQueriesData<SMPWSchedule[]>({ queryKey: [QueryKeys.SMPWZoneSchedules] }, (old) =>
    old ? old.filter((s) => !schedules.includes(s)) : [],
  );
}

export const updateSMPWCongregations = (queryClient: QueryClient, newCong: Congregation) => {
  queryClient.setQueryData<Congregation[]>([QueryKeys.SMPWNearbyCongregations], (old) => {
    if (!old) return [newCong];
    if (!old.some((zd) => zd.id === newCong.id)) return [...old, newCong];
    return old.map((zd) => (zd.id === newCong.id ? newCong : zd));
  });
};

export const updateSMPWReports = (queryClient: QueryClient, report: SMPWReport) => {
  const from = dateToString(dayjs(report.date).startOf("month").toDate());
  const to = dateToString(dayjs(report.date).endOf("month").toDate());
  queryClient.setQueryData<SMPWReport[]>([QueryKeys.SMPWReports, from, to], (old) => {
    if (!old) return [report];
    if (!old.some((zd) => zd.id === report.id)) return [...old, report];
    return old.map((zd) => (zd.id === report.id ? report : zd));
  });
};

export function deleteFromSMPWReportsCache(queryClient: QueryClient, report: SMPWReport) {
  const from = dateToString(dayjs(report.date).startOf("month").toDate());
  const to = dateToString(dayjs(report.date).endOf("month").toDate());
  queryClient.setQueryData<SMPWReport[]>([QueryKeys.SMPWReports, from, to], (old) =>
    old ? old.filter((r) => r.id !== report.id) : [],
  );
}

export const updateSMPWLocationMasksCache = (
  queryClient: QueryClient,
  userId: number,
  newMasks: SMPWUserLocationAssignmentMask[],
) => {
  queryClient.setQueryData<SMPWUserLocationAssignmentMask[]>([QueryKeys.SMPWUserLocationAssignmentMask], (old) => {
    const withoutUser = (old ?? []).filter((m) => m.user_id !== userId);
    return [...withoutUser, ...newMasks];
  });
};
